import React from 'react';
import { Typography, Box } from '@material-ui/core';
import CheckCircle from '@material-ui/icons/CheckCircleOutlineOutlined';
import { ActiveStep } from '../OnboardingContext';
import { OnboardingMessageKey } from '../../../translations/messages';
import { OnboardingLanguage } from '../../../translations';
import { OnboardingColors } from '../colors';
import { TFunction } from './journeySteps';
import { QuizStep } from './QuizStep';
import { journey3Quiz } from './quizQuestions';

/* ================================================================
 *  Step metadata — shared across all languages
 * ================================================================ */

interface StepDef {
  sectionKey: OnboardingMessageKey;
  titleKey: OnboardingMessageKey;
  stepIndex: number;
  totalSteps: number;
  targetSelector?: string;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  navigateTo?: string;
  customActions?: boolean;
  stepType?: 'quiz' | 'playground';
}

const journey3StepDefs: StepDef[] = [
  /* PHASE 1: Check your logs first (2 steps) */
  { sectionKey: 'sectionJ3CheckLogs', titleKey: 'stepJ3StartGrafana', stepIndex: 1, totalSteps: 2 },
  { sectionKey: 'sectionJ3CheckLogs', titleKey: 'stepJ3WhatToLookLogs', stepIndex: 2, totalSteps: 2 },

  /* PHASE 2: Check your deploy (4 steps) */
  { sectionKey: 'sectionJ3CheckDeploy', titleKey: 'stepJ3WhenNotInLogs', stepIndex: 1, totalSteps: 4 },
  { sectionKey: 'sectionJ3CheckDeploy', titleKey: 'stepJ3GitOpsMisconfiguration', stepIndex: 2, totalSteps: 4 },
  { sectionKey: 'sectionJ3CheckDeploy', titleKey: 'stepJ3PipelineFailures', stepIndex: 3, totalSteps: 4 },
  { sectionKey: 'sectionJ3CheckDeploy', titleKey: 'stepJ3ImageNotUpdating', stepIndex: 4, totalSteps: 4 },

  /* PHASE 3: When to ask for help (2 steps) */
  { sectionKey: 'sectionJ3AskHelp', titleKey: 'stepJ3OpenTicket', stepIndex: 1, totalSteps: 2 },
  { sectionKey: 'sectionJ3AskHelp', titleKey: 'stepJ3Journey3Complete', stepIndex: 2, totalSteps: 2 },

  /* PHASE 4: Quiz (1 step, custom actions) */
  { sectionKey: 'sectionQuiz', titleKey: 'stepQuiz', stepIndex: 1, totalSteps: 1, customActions: true, stepType: 'quiz' },
];

/* ================================================================
 *  Content per language
 * ================================================================ */

type ContentGetter = (c: OnboardingColors) => React.ReactNode[];

const journey3ContentByLanguage: Record<OnboardingLanguage, ContentGetter> = {
  en: (c) => getJourney3ContentEN(c),
  es: (c) => getJourney3ContentES(c),
  'pt-BR': (c) => getJourney3ContentPtBR(c),
};

/* ================================================================
 *  Public API
 * ================================================================ */

export const getJourney3Steps = (
  journeyIndex: number,
  journeyTitle: string,
  t: TFunction = key => key,
  language: OnboardingLanguage = 'en',
  colors?: OnboardingColors,
): ActiveStep[] => {
  const journeyLabel = `${t('journeyLabel', { index: journeyIndex + 1 })}: ${journeyTitle}`;

  switch (journeyIndex) {
    case 2: {
      const getContent = journey3ContentByLanguage[language] ?? journey3ContentByLanguage.en;
      const fallbackColors: OnboardingColors = colors ?? ({} as OnboardingColors);
      const contents = getContent(fallbackColors);

      // Build quiz content for the last step
      const questions = journey3Quiz[language] ?? journey3Quiz.en;
      const quizContent = (
        <QuizStep
          questions={questions}
          minCorrect={6}
          labels={{
            questionOf: t('quizQuestionOf'),
            back: t('back'),
            confirm: t('quizConfirm'),
            continueLabel: t('continueLabel'),
            correctAnswer: t('quizCorrectAnswer'),
            quizPassed: t('quizPassed'),
            quizFailed: t('quizFailed'),
            correctAnswers: t('quizCorrectAnswers'),
            nextJourneyUnlocked: t('quizNextSteps'),
            continueToJourney: t('quizFinishOnboarding'),
            tryAgain: t('quizTryAgain'),
            failedMessage: t('quizFailedMessage'),
            reviewJourney: t('quizReviewJourney'),
          }}
        />
      );
      const allContents = [...contents, quizContent];

      return journey3StepDefs.map((def, i) => ({
        journeyIndex,
        journeyTitle: journeyLabel,
        sectionTitle: t(def.sectionKey),
        stepIndex: def.stepIndex,
        totalSteps: def.totalSteps,
        stepTitle: t(def.titleKey),
        stepContent: allContents[i],
        ...(def.targetSelector && { targetSelector: def.targetSelector }),
        ...(def.placement && { placement: def.placement }),
        ...(def.navigateTo && { navigateTo: def.navigateTo }),
        ...(def.customActions && { customActions: def.customActions }),
        ...(def.stepType && { stepType: def.stepType }),
      }));
    }
    default:
      return [];
  }
};

/* ================================================================
 *  Journey 3 — English (8 steps: 2+4+2)
 * ================================================================ */

const getJourney3ContentEN = (c: OnboardingColors): React.ReactNode[] => [
  /* PHASE 1: Check your logs first */
  <Box key="en-1">
    <Typography variant="body2" paragraph>
      When something goes wrong with your application, the first place to investigate is Grafana.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Go to the right instance</strong><br />
      Open the Grafana instance for the environment where the problem is happening:
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Development:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-dv.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Test/Homolog:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-ts.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Production:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana.vsa.nestle.biz</code></Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Filter your logs</strong><br />
      Open the Explore section, select the correct Datasource, and filter by your application name. Use a short time range around when the problem started.
    </Typography>
    <Typography variant="body2" paragraph>
      Choose Builder mode to use the visual interface, or Code mode if you already know LogQL. Click Run Query to see the results.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg || '#d1ecf1', padding: 12, borderRadius: 4, marginTop: 16 }}>
      <strong>Tip:</strong> Focus on the timestamps that match when the problem was reported. Start narrow and expand if needed.
    </Typography>
  </Box>,

  <Box key="en-2">
    <Typography variant="body2" paragraph>
      When reading logs, focus on these patterns:
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Application errors</strong><br />
      Exceptions, stack traces, "connection refused", timeouts, HTTP 500 errors. These indicate a bug or misconfiguration in your application code.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Startup failures</strong><br />
      The application failed to initialize. Look for "failed to start", "port already in use", or missing environment variables. This often means misconfiguration in your component file.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Health check failures</strong><br />
      The /health endpoint is returning errors. Kubernetes will restart the pod if liveness fails, and stop sending traffic if readiness fails.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      If the problem appears in the logs, it is your team's responsibility to fix it in the code or configuration.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      If you do not see any logs, or the pod never started, the problem is likely before initialization. Check your deployment in the next phase.
    </Typography>
  </Box>,

  /* PHASE 2: Check your deploy */
  <Box key="en-3">
    <Typography variant="body2" paragraph>
      If the problem is not showing in the Grafana logs and is happening before the pods initialize, go to ArgoCD.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Open your application in ArgoCD</strong><br />
      Go to <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>argocd-nlar.vsa.nestle.biz</code>, use label filters to find your application (env=dv, env=ts, or env=pr), and click on it.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>What to check</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Health Status:</strong> is it Degraded instead of Healthy?</Typography>
      <Typography variant="body2">• <strong>Sync Status:</strong> is it OutOfSync?</Typography>
      <Typography variant="body2">• <strong>App Conditions:</strong> are there any errors listed?</Typography>
      <Typography variant="body2">• <strong>Resource Tree:</strong> click on the Pod to see the Summary, Events, and Logs tabs</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      The two most common causes are GitOps misconfiguration and pipeline failures. The next steps cover both.
    </Typography>
  </Box>,

  <Box key="en-4">
    <Typography variant="body2" paragraph>
      The most common cause of deploy failures: something is wrong in your GitOps YAML file.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Helm Chart Rendering errors</strong><br />
      ArgoCD uses Helm to parse your files. If a file is missing or has invalid YAML, you will see an error in the App Conditions section. Click on the error to see which file has the problem.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Common mistakes</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Indentation errors (YAML is strict about spaces)</Typography>
      <Typography variant="body2">• Missing required fields (name and aadpodidbinding)</Typography>
      <Typography variant="body2">• Sensitive values in the env block instead of secrets</Typography>
      <Typography variant="body2">• Tabs instead of spaces</Typography>
      <Typography variant="body2">• Invalid syntax (missing colons, wrong nesting)</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>How to fix</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16 }}>
      <Typography variant="body2">1. Open the file in the GitOps repository</Typography>
      <Typography variant="body2">2. Run it through YAML Lint to find the error</Typography>
      <Typography variant="body2">3. Fix the issue and commit to main</Typography>
      <Typography variant="body2">4. ArgoCD will auto-sync once the fix is in</Typography>
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Always validate your YAML before committing. This prevents most GitOps-related deploy failures.
    </Typography>
  </Box>,

  <Box key="en-5">
    <Typography variant="body2" paragraph>
      If the CI pipeline failed, the new image was never generated and the deploy did not happen.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>How to check</strong><br />
      Open the pipeline runs in GitHub Actions (or Azure DevOps). Find the failed run: stages with errors appear in red. Click on the failed stage to see the detailed logs.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Common failures by stage:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Tests:</strong> Unit tests broke due to new code, or SonarQube coverage dropped below 95%. Fix tests or improve coverage.</Typography>
      <Typography variant="body2">• <strong>Build:</strong> "No such image" error: usually Docker Hub instability, expired credentials, or network restrictions. May require a Support Team ticket.</Typography>
      <Typography variant="body2">• <strong>Update GitOps:</strong> The pipeline could not update the bom.yaml. Check for connectivity issues with the GitOps repository.</Typography>
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      All pipeline stages must be green. The entire team is responsible for keeping the CI pipeline healthy.
    </Typography>
  </Box>,

  <Box key="en-6">
    <Typography variant="body2" paragraph>
      The pipeline ran successfully but ArgoCD is still showing the old version? This is a sync issue.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>How to verify</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">1. In the pipeline runs, find the step "Update version &lt;app-name&gt; to version &lt;version&gt;" in dv to confirm the new version was written.</Typography>
      <Typography variant="body2">2. In ArgoCD, open the Deploy manifest and check the Desired Manifest tab to confirm the new version is being deployed.</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>How to fix</strong><br />
      If the version in ArgoCD does not match the pipeline output, use Hard Refresh: click the Refresh button and select Hard Refresh. This forces ArgoCD to fetch the latest data from the GitOps repository. Sync happens automatically after.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      If the pod shows ImagePullBackOff, the image may not exist in the Azure Container Registry. Verify the Build stage of the pipeline completed successfully.
    </Typography>
  </Box>,

  /* PHASE 3: When to ask for help */
  <Box key="en-7">
    <Typography variant="body2" paragraph>
      If after checking the logs in Grafana and the deploy in ArgoCD the problem does not match any of these scenarios, open a ticket for the Platform Team.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Before opening a ticket, make sure you checked:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">1. Grafana logs for application errors</Typography>
      <Typography variant="body2">2. ArgoCD or Health/Sync status and App Conditions</Typography>
      <Typography variant="body2">3. Pipeline logs for failed stages</Typography>
      <Typography variant="body2">4. GitOps YAML files for misconfiguration</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>When opening the ticket</strong><br />
      Attach screenshots of the relevant panels (ArgoCD status, pipeline logs, Grafana queries) with additional context about what you already investigated.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      The more context you provide in the ticket, the faster the Platform Team can help. Screenshots and timestamps make a significant difference.
    </Typography>
  </Box>,

  <Box key="en-8">
    <Box style={{ textAlign: 'center', paddingTop: 16 }}>
      <CheckCircle style={{ fontSize: 56, color: c.success, marginBottom: 8 }} />
      <Typography variant="h6">Journey 3 complete!</Typography>
    </Box>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      Congratulations! You have completed Journey 3: When Something Goes Wrong.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>You now know how to:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Navigate the IDP, find your project, and understand platform tools (Journey 1)</Typography>
      <Typography variant="body2">• Take your code from a push to a running application, configure GitOps files, and monitor your deploys (Journey 2)</Typography>
      <Typography variant="body2">• Investigate problems using Grafana and ArgoCD, fix common issues, and know when to escalate (Journey 3)</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      You now have the skills to independently manage your deployments on the IDP. Complete the final quiz to validate your knowledge. You need 90% to pass. Once approved, you will have full access to the platform to start working on your project.
    </Typography>
  </Box>,
];

/* ================================================================
 *  Journey 3 — Portuguese pt-BR (8 steps: 2+4+2)
 * ================================================================ */

const getJourney3ContentPtBR = (c: OnboardingColors): React.ReactNode[] => [
  /* PHASE 1: Check your logs first */
  <Box key="pt-1">
    <Typography variant="body2" paragraph>
      Quando algo dá errado com sua aplicação, o primeiro lugar para investigar é o Grafana.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Vá para a instância correta</strong><br />
      Abra a instância do Grafana para o ambiente onde o problema está acontecendo:
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Desenvolvimento:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-dv.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Teste/Homologação:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-ts.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Produção:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana.vsa.nestle.biz</code></Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Filtre seus logs</strong><br />
      Abra a seção Explore, selecione o Datasource correto e filtre pelo nome da sua aplicação. Use um intervalo de tempo curto em torno de quando o problema começou.
    </Typography>
    <Typography variant="body2" paragraph>
      Escolha o modo Builder para usar a interface visual, ou o modo Code se já conhecer LogQL. Clique em Run Query para ver os resultados.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg || '#d1ecf1', padding: 12, borderRadius: 4, marginTop: 16 }}>
      <strong>Dica:</strong> Foque nos timestamps que correspondem a quando o problema foi reportado. Comece com um intervalo curto e expanda se necessário.
    </Typography>
  </Box>,

  <Box key="pt-2">
    <Typography variant="body2" paragraph>
      Ao ler os logs, foque nestes padrões:
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Erros da aplicação</strong><br />
      Exceções, stack traces, "connection refused", timeouts, erros HTTP 500. Indicam um bug ou misconfiguração no código da sua aplicação.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Falhas de inicialização</strong><br />
      A aplicação falhou ao inicializar. Procure por "failed to start", "port already in use", ou variáveis de ambiente faltando. Geralmente significa misconfiguração no seu arquivo de componente.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Falhas de health check</strong><br />
      O endpoint /health está retornando erros. Kubernetes vai reiniciar o pod se liveness falhar, e vai parar de enviar tráfego se readiness falhar.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Se o problema aparece nos logs, é responsabilidade do seu time corrigi-lo no código ou na configuração.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Se você não vê nenhum log, ou o pod nunca iniciou, o problema provavelmente está antes da inicialização. Verifique seu deploy na próxima fase.
    </Typography>
  </Box>,

  /* PHASE 2: Check your deploy */
  <Box key="pt-3">
    <Typography variant="body2" paragraph>
      <strong>Quando não está nos logs</strong>
    </Typography>
    <Typography variant="body2" paragraph>
      Se o problema não está aparecendo nos logs do Grafana e acontecendo antes dos pods iniciarem, vá para ArgoCD.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Abra sua aplicação no ArgoCD</strong><br />
      Vá para <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>argocd-nlar.vsa.nestle.biz</code>, use filtros de label para encontrar sua aplicação (env=dv, env=ts, ou env=pr), e clique nela.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>O que verificar</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Status de Saúde:</strong> está Degradado ao invés de Saudável?</Typography>
      <Typography variant="body2">• <strong>Status de Sincronização:</strong> está OutOfSync?</Typography>
      <Typography variant="body2">• <strong>Condições da Aplicação:</strong> há erros listados?</Typography>
      <Typography variant="body2">• <strong>Árvore de Recursos:</strong> clique no Pod para ver as abas Summary, Events e Logs</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      As duas causas mais comuns são erros de configuração GitOps e falhas de pipeline. Os próximos passos cobrem ambas.
    </Typography>
  </Box>,

  <Box key="pt-4">
    <Typography variant="body2" paragraph>
      A causa mais comum de falhas de deploy: algo está errado no seu arquivo YAML do GitOps.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Erros de renderização do Helm Chart</strong><br />
      ArgoCD usa Helm para fazer parse de seus arquivos. Se um arquivo está faltando ou tem YAML inválido, você verá um erro na seção App Conditions. Clique no erro para ver qual arquivo tem o problema.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Erros comuns</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Erros de indentação (YAML é rigoroso com espaços)</Typography>
      <Typography variant="body2">• Campos obrigatórios faltando (name e aadpodidbinding)</Typography>
      <Typography variant="body2">• Valores sensíveis no bloco env ao invés de secrets</Typography>
      <Typography variant="body2">• Tabs ao invés de espaços</Typography>
      <Typography variant="body2">• Sintaxe inválida (dois-pontos faltando, nesting errado)</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Como corrigir</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16 }}>
      <Typography variant="body2">1. Abra o arquivo no repositório GitOps</Typography>
      <Typography variant="body2">2. Execute através de YAML Lint para encontrar o erro</Typography>
      <Typography variant="body2">3. Corrija o problema e faça commit para main</Typography>
      <Typography variant="body2">4. ArgoCD fará auto-sync uma vez que o fix esteja in</Typography>
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Sempre valide seu YAML antes de fazer commit. Isso previne a maioria das falhas de deploy relacionadas a GitOps.
    </Typography>
  </Box>,

  <Box key="pt-5">
    <Typography variant="body2" paragraph>
      Se o pipeline de CI falhou, a nova imagem nunca foi gerada e o deploy não aconteceu.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Como verificar</strong><br />
      Abra as execuções do pipeline no GitHub Actions (ou Azure DevOps). Encontre a execução que falhou: estágios com erros aparecem em vermelho. Clique no estágio que falhou para ver os logs detalhados.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Falhas comuns por estágio:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Testes:</strong> Testes unitários quebraram devido a novo código, ou cobertura SonarQube caiu abaixo de 95%. Corrija testes ou melhore cobertura.</Typography>
      <Typography variant="body2">• <strong>Build:</strong> Erro "No such image": geralmente instabilidade do Docker Hub, credenciais expiradas, ou restrições de rede. Pode exigir ticket do Support Team.</Typography>
      <Typography variant="body2">• <strong>Atualizar GitOps:</strong> O pipeline não conseguiu atualizar o bom.yaml. Verifique problemas de conectividade com o repositório GitOps.</Typography>
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Todos os estágios do pipeline devem estar verde. Todo o time é responsável por manter o pipeline CI saudável.
    </Typography>
  </Box>,

  <Box key="pt-6">
    <Typography variant="body2" paragraph>
      O pipeline executou com sucesso mas ArgoCD ainda está mostrando a versão antiga? Isso é um problema de sincronização.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Como verificar</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">1. Nas execuções do pipeline, encontre o passo "Update version &lt;app-name&gt; to version &lt;version&gt;" em dv para confirmar que a nova versão foi escrita.</Typography>
      <Typography variant="body2">2. Em ArgoCD, abra o Deploy manifest e verifique a aba Desired Manifest para confirmar que a nova versão está sendo deployada.</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Como corrigir</strong><br />
      Se a versão em ArgoCD não corresponde à saída do pipeline, use Hard Refresh: clique no botão Refresh e selecione Hard Refresh. Isso força ArgoCD a buscar os dados mais recentes do repositório GitOps. A sincronização acontece automaticamente depois.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Se o pod mostra ImagePullBackOff, a imagem pode não existir no Azure Container Registry. Verifique se o estágio Build do pipeline completou com sucesso.
    </Typography>
  </Box>,

  /* PHASE 3: When to ask for help */
  <Box key="pt-7">
    <Typography variant="body2" paragraph>
      Se após verificar os logs no Grafana e o deploy no ArgoCD o problema não corresponde a nenhum um desses cenários, abra um ticket para o Time de Plataforma.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Antes de abrir um ticket, certifique-se de que você verificou:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">1. Logs do Grafana para erros da aplicação</Typography>
      <Typography variant="body2">2. ArgoCD para status de Health/Sync e App Conditions</Typography>
      <Typography variant="body2">3. Logs do pipeline para estágios que falharam</Typography>
      <Typography variant="body2">4. Arquivos YAML do GitOps para misconfigurações</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Ao abrir o ticket</strong><br />
      Anexe screenshots dos painéis relevantes (status do ArgoCD, logs do pipeline, queries do Grafana) com contexto adicional sobre o que você já investigou.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Quanto mais contexto você fornecer no ticket, mais rápido o Time de Plataforma pode ajudar. Screenshots e timestamps fazem uma diferença significativa.
    </Typography>
  </Box>,

  <Box key="pt-8">
    <Box style={{ textAlign: 'center', paddingTop: 16 }}>
      <CheckCircle style={{ fontSize: 56, color: c.success, marginBottom: 8 }} />
      <Typography variant="h6">Jornada 3 completa!</Typography>
    </Box>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      Parabéns! Você completou todas as três Jornadas de Desenvolvedor.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Você agora sabe como:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Navegar pela IDP, encontrar seu projeto e entender as ferramentas da plataforma (Jornada 1)</Typography>
      <Typography variant="body2">• Levar seu código de um push para uma aplicação rodando, configurar arquivos do GitOps e monitorar seus deploys (Jornada 2)</Typography>
      <Typography variant="body2">• Investigar problemas usando Grafana e ArgoCD, corrigir problemas comuns e saber quando escalar (Jornada 3)</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      Agora você tem as habilidades para gerenciar independentemente seus deployments na IDP. Complete o quiz final para validar seu conhecimento. Você precisa de 90% para passar. Uma vez aprovado, você terá acesso completo à plataforma para começar a trabalhar no seu projeto.
    </Typography>
  </Box>,
];

/* ================================================================
 *  Journey 3 — Spanish (8 steps: 2+4+2)
 * ================================================================ */

const getJourney3ContentES = (c: OnboardingColors): React.ReactNode[] => [
  /* PHASE 1: Check your logs first */
  <Box key="es-1">
    <Typography variant="body2" paragraph>
      Cuando algo sale mal con tu aplicación, el primer lugar para investigar es Grafana.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Ve a la instancia correcta</strong><br />
      Abre la instancia de Grafana para el entorno donde está ocurriendo el problema:
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Desarrollo:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-dv.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Test/Homologación:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana-ts.vsa.nestle.biz</code></Typography>
      <Typography variant="body2">• <strong>Producción:</strong> <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>grafana.vsa.nestle.biz</code></Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Filtra tus logs</strong><br />
      Abre la sección Explore, selecciona el Datasource correcto y filtra por el nombre de tu aplicación. Usa un rango de tiempo corto alrededor de cuando comenzó el problema.
    </Typography>
    <Typography variant="body2" paragraph>
      Elige el modo Builder para usar la interfaz visual, o el modo Code si ya conoces LogQL. Haz clic en Run Query para ver los resultados.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c?.infoBoxBg || '#d1ecf1', padding: 12, borderRadius: 4, marginTop: 16 }}>
      <strong>Consejo:</strong> Enfócate en los timestamps que coincidan con cuando se reportó el problema. Comienza con un rango corto y expande si es necesario.
    </Typography>
  </Box>,

  <Box key="es-2">
    <Typography variant="body2" paragraph>
      Al leer los logs, enfócate en estos patrones:
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Errores de aplicación</strong><br />
      Excepciones, stack traces, "connection refused", timeouts, errores HTTP 500. Indican un bug o mala configuración en el código de tu aplicación.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Fallos de inicialización</strong><br />
      La aplicación falló al inicializar. Busca "failed to start", "port already in use", o variables de entorno faltantes. Esto generalmente significa mala configuración en tu archivo de componente.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Fallos de health check</strong><br />
      El endpoint /health está retornando errores. Kubernetes reiniciará el pod si liveness falla, y dejará de enviar tráfico si readiness falla.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Si el problema aparece en los logs, es responsabilidad de tu equipo corregirlo en el código o configuración.
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Si no ves ningún log, o el pod nunca inició, el problema probablemente está antes de la inicialización. Verifica tu despliegue en la siguiente fase.
    </Typography>
  </Box>,

  /* PHASE 2: Check your deploy */
  <Box key="es-3">
    <Typography variant="body2" paragraph>
      Si el problema no está apareciendo en los logs de Grafana y está ocurriendo antes de que los pods se inicien, ve a ArgoCD.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Abre tu aplicación en ArgoCD</strong><br />
      Ve a <code style={{ fontFamily: 'monospace', backgroundColor: c?.placeholderBg || '#f5f5f5', padding: '2px 6px', borderRadius: 2 }}>argocd-nlar.vsa.nestle.biz</code>, usa filtros de etiqueta para encontrar tu aplicación (env=dv, env=ts, o env=pr), y haz clic en ella.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Qué verificar</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Estado de Salud:</strong> ¿está Degradado en lugar de Saludable?</Typography>
      <Typography variant="body2">• <strong>Estado de Sincronización:</strong> ¿está OutOfSync?</Typography>
      <Typography variant="body2">• <strong>Condiciones de la Aplicación:</strong> ¿hay errores listados?</Typography>
      <Typography variant="body2">• <strong>Árbol de Recursos:</strong> haz clic en el Pod para ver las pestañas Summary, Events y Logs</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      Las dos causas más comunes son la mala configuración en GitOps y las fallas de pipeline. Los próximos pasos cubren ambas.
    </Typography>
  </Box>,

  <Box key="es-4">
    <Typography variant="body2" paragraph>
      La causa más común de fallas de implementación: algo está mal en tu archivo YAML de GitOps.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Errores de renderización del Gráfico de Helm</strong><br />
      ArgoCD usa Helm para analizar tus archivos. Si falta un archivo o tiene YAML inválido, verás un error en la sección App Conditions. Haz clic en el error para ver qué archivo tiene el problema.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Errores comunes</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Errores de indentación (YAML es estricto con los espacios)</Typography>
      <Typography variant="body2">• Campos requeridos faltantes (nombre y aadpodidbinding)</Typography>
      <Typography variant="body2">• Valores sensibles en el bloque env en lugar de secrets</Typography>
      <Typography variant="body2">• Tabs en lugar de espacios</Typography>
      <Typography variant="body2">• Sintaxis inválida (dos puntos faltantes, anidamiento incorrecto)</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Cómo solucionar</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16 }}>
      <Typography variant="body2">1. Abre el archivo en el repositorio GitOps</Typography>
      <Typography variant="body2">2. Ejecútalo a través de YAML Lint para encontrar el error</Typography>
      <Typography variant="body2">3. Corrige el problema e haz commit a main</Typography>
      <Typography variant="body2">4. ArgoCD hará auto-sincronización una vez que la solución esté in</Typography>
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Siempre valida tu YAML antes de hacer commit. Esto evita la mayoría de las fallas de implementación relacionadas con GitOps.
    </Typography>
  </Box>,

  <Box key="es-5">
    <Typography variant="body2" paragraph>
      Si el pipeline de CI falló, la nueva imagen nunca se generó y el despliegue no ocurrió.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Cómo verificar</strong><br />
      Abre las ejecuciones del pipeline en GitHub Actions (o Azure DevOps). Encuentra la ejecución fallida: las etapas con errores aparecen en rojo. Haz clic en la etapa fallida para ver los logs detallados.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Fallas comunes por etapa:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• <strong>Pruebas:</strong> Las pruebas unitarias se rompieron debido a código nuevo, o la cobertura de SonarQube cayó por debajo del 95%. Corrige las pruebas o mejora la cobertura.</Typography>
      <Typography variant="body2">• <strong>Build:</strong> Error "No such image": generalmente inestabilidad de Docker Hub, credenciales expiradas o restricciones de red. Puede requerir un ticket del Support Team.</Typography>
      <Typography variant="body2">• <strong>Update GitOps:</strong> El pipeline no pudo actualizar el bom.yaml. Verifica los problemas de conectividad con el repositorio GitOps.</Typography>
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Todas las etapas del pipeline deben estar en verde. Todo el equipo es responsable de mantener el pipeline de CI saludable.
    </Typography>
  </Box>,

  <Box key="es-6">
    <Typography variant="body2" paragraph>
      ¿El pipeline se ejecutó correctamente pero ArgoCD sigue mostrando la versión anterior? Este es un problema de sincronización.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Cómo verificar</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">1. En las ejecuciones del pipeline, encuentra el paso "Update version &lt;app-name&gt; to version &lt;version&gt;" en dv para confirmar que se escribió la nueva versión.</Typography>
      <Typography variant="body2">2. En ArgoCD, abre el Deploy manifest y verifica la pestaña Desired Manifest para confirmar que la nueva versión se está implementando.</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Cómo solucionar</strong><br />
      Si la versión en ArgoCD no coincide con la salida del pipeline, usa Hard Refresh: haz clic en el botón Refresh y selecciona Hard Refresh. Esto fuerza a ArgoCD a obtener los datos más recientes del repositorio GitOps. La sincronización ocurre automáticamente después.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Si el pod muestra ImagePullBackOff, la imagen puede no existir en Azure Container Registry. Verifica que la etapa Build del pipeline se completó correctamente.
    </Typography>
  </Box>,

  /* PHASE 3: When to ask for help */
  <Box key="es-7">
    <Typography variant="body2" paragraph>
      Si después de verificar los logs en Grafana y el despliegue en ArgoCD el problema no coincide con ninguno de estos escenarios, abre un ticket para el Equipo de Plataforma.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Antes de abrir un ticket, asegúrate de que verificaste:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">1. Logs de Grafana para errores de aplicación</Typography>
      <Typography variant="body2">2. ArgoCD para estado de Health/Sync y App Conditions</Typography>
      <Typography variant="body2">3. Logs del pipeline para etapas que fallaron</Typography>
      <Typography variant="body2">4. Archivos YAML de GitOps para configuraciones incorrectas</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 12 }}>
      <strong>Al abrir el ticket</strong><br />
      Adjunta capturas de pantalla de los paneles relevantes (estado de ArgoCD, logs del pipeline, consultas de Grafana) con contexto adicional sobre lo que ya investigaste.
    </Typography>
    <Typography variant="body2" style={{ backgroundColor: c.warningBannerBg, padding: 12, borderRadius: 4, marginTop: 16 }}>
      Cuanto más contexto proporciones en el ticket, más rápido el Equipo de Plataforma puede ayudarte. Las capturas de pantalla y los timestamps marcan una diferencia significativa.
    </Typography>
  </Box>,

  <Box key="es-8">
    <Box style={{ textAlign: 'center', paddingTop: 16 }}>
      <CheckCircle style={{ fontSize: 56, color: c.success, marginBottom: 8 }} />
      <Typography variant="h6">¡Jornada 3 completada!</Typography>
    </Box>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      ¡Felicidades! Has completado los tres Viajes de Desarrollador.
    </Typography>
    <Typography variant="body2" paragraph>
      <strong>Ahora sabes cómo:</strong>
    </Typography>
    <Typography variant="body2" component="div" style={{ paddingLeft: 16, marginTop: 8 }}>
      <Typography variant="body2">• Navegar por la IDP, encontrar tu proyecto y entender las herramientas de la plataforma (Viaje 1)</Typography>
      <Typography variant="body2">• Llevar tu código de un push a una aplicación en ejecución, configurar archivos GitOps y monitorear tus despliegues (Viaje 2)</Typography>
      <Typography variant="body2">• Investigar problemas usando Grafana y ArgoCD, corregir problemas comunes y saber cuándo escalar (Viaje 3)</Typography>
    </Typography>
    <Typography variant="body2" paragraph style={{ marginTop: 16 }}>
      Ahora tienes las habilidades para gestionar independientemente tus despliegues en la IDP. Completa el quiz final para validar tu conocimiento. Necesitas 90% para pasar. Una vez aprobado, tendrás acceso completo a la plataforma para comenzar a trabajar en tu proyecto.
    </Typography>
  </Box>,
];
